// © Microsoft Corporation. All rights reserved.

import { FASTElement, attr, observable } from "@microsoft/fast-element";
import { FeedNavigationData, FeedNavigationItem, FeedNavigationLocalizedStrings, NavigationGroup, NavigationItemInternal } from "./Interfaces";

import { MsnNavBar } from "@msnews/msn-nav-bar";
import { isEqual } from "lodash-es";

export class FeedNavigation extends FASTElement {
    @attr public hidePersonalizeButton = false;
    @observable public displayBreadCrumb: boolean;
    @observable public showOneParentBreadCrumb: boolean;
    @observable public showPipeSeparator: boolean;
    @observable public navGroups: NavigationGroup;
    @observable public selectedNavItem: FeedNavigationItem;
    @observable public selectedNavMenuItem: FeedNavigationItem;
    @observable public interestsNavData: FeedNavigationItem;
    @observable public localizedStrings: FeedNavigationLocalizedStrings = {} as FeedNavigationLocalizedStrings;
    @observable public overflowTelemetryTag: string;
    @observable public personalizeButtonTelemetryTag: string;

    /** Only show the personalize button when this is true. Used to hide the personalize button while in the interests hub */
    @observable public showPersonalizeButton = true;

    public feedNav: MsnNavBar;

    protected navItemMap = new Map<string, NavigationItemInternal>();

    /**
     * The connected callback handler
     */
    public connectedCallback() {
        super.connectedCallback();

        if (!this.navGroups) {
            return;
        }

        this.buildNavMap();
        this.setNavItems();
    }

    /**
     * Personalize link click handler
     */
    public onPersonalizeClick = (): void => {
        this.$emit("change", { selectedNav: this.interestsNavData });
    };

    /**
     * Click handler for nav items, listens to change event in FAST feed navigation web component
     */
    public onNavItemClick = (event: CustomEvent): void => {
        const { selectedNav } = event.detail || {};

        if (!selectedNav || (selectedNav.renderInfo && selectedNav.renderInfo.openInNewTab == true)) {
            return;
        }

        this.$emit("change", { selectedNav });
        event.stopPropagation();
    };

    /** Selected Nav change handler */
    private selectedNavItemChanged(oldValue: FeedNavigationItem, newValue: FeedNavigationItem): void {
        if (!newValue || (oldValue && oldValue.id) === newValue.id) {
            return;
        }

        this.setNavItems();
    }

    /** Selected Nav change handler */
    private selectedNavMenuItemChanged(oldValue: FeedNavigationItem, newValue: FeedNavigationItem): void {
        if (!newValue || (oldValue && oldValue.id) === newValue.id) {
            return;
        }

        this.setNavItems();
    }

    /** Localized strings change handler */
    private localizedStringsChanged(oldValue, newValue): void {
        if (!this.feedNav) {
            return;
        }

        this.feedNav.localizedStrings = this.localizedStrings || {} as FeedNavigationLocalizedStrings;
    }

    /** showPipeSeparator change handler */
    private showPipeSeparatorChanged(oldValue: boolean, newValue: boolean): void {
        if (oldValue === newValue) {
            return;
        }

        this.setNavItems();
    }

    /** display Bread crumb change handler */
    private displayBreadCrumbChanged(oldValue: boolean, newValue: boolean): void {
        if (oldValue === newValue) {
            return;
        }

        this.setNavItems();
    }

    /** display Bread crumb change handler */
    private showOneParentBreadCrumbChanged(oldValue: boolean, newValue: boolean): void {
        if (oldValue === newValue) {
            return;
        }

        this.setNavItems();
    }

    /** InterestsNavData change handler */
    private interestsNavDataChanged(): void {
        if (!this.interestsNavData) {
            return;
        }

        this.navItemMap.set(this.interestsNavData.id, this.interestsNavData);
        this.mapChildItems(this.interestsNavData.children, this.interestsNavData.id);
    }

    /**
     * NavGroups data changed handler, this is internally invoked by Observable implemetation when data changes
     * @param previousValue - previous value
     * @param currentValue - the current value of data input object
     */
    private navGroupsChanged(previousValue: FeedNavigationData, currentValue: FeedNavigationData): void {
        if (isEqual(previousValue, currentValue)) {
            return;
        }

        this.buildNavMap();
        this.setNavItems();
    }

    /**
     * Sets the input data for FAST feed navigation web component
     */
    protected setNavItems() {
        if (!this.navGroups || !this.feedNav) {
            return;
        }

        let data = this.getContextualNavItems();

        if (!data) {
            const { home, feeds } = this.navGroups;
            data = [{ ...home, isActive: (this.selectedNavItem && this.selectedNavItem.id) !== (this.interestsNavData && this.interestsNavData.id) }, ...feeds];
        }

        this.feedNav.data = data;
    }

    /**
     * Returns the nav items for the current context
     */
    protected getContextualNavItems(): NavigationItemInternal[] {
        // TODO: Add fast following for avoiding having to specify 2 params selectedNavItem and selectedNavMenu Item
        const selectedNavId = this.selectedNavItem && this.selectedNavItem.id;
        const selectedNavMenuId = this.selectedNavMenuItem && this.selectedNavMenuItem.id;
        if (!selectedNavMenuId || !selectedNavId) {
            return;
        }

        const setActiveItem = (navItems: NavigationItemInternal[]) => navItems.map(item => {
            item.isActive = item.id === selectedNavId;
            return item;
        });

        const selectedNav = this.navItemMap.get(selectedNavMenuId);
        const { home, feeds } = this.navGroups;
        // Check if selected nav is interests page and handle accordingly
        if (this.interestsNavData &&
            (selectedNavId === this.interestsNavData.id || (selectedNav && selectedNav.parentId === this.interestsNavData.id))) {
            // If interests has child items, show them
            const interestsChildItems = this.interestsNavData.children;
            if (interestsChildItems && interestsChildItems.length) {
                this.showPersonalizeButton = false;
                let activeItemList: FeedNavigationItem[] = home != null ? [{ ...home, showPipeSeparator: true }] : [];
                activeItemList = activeItemList.concat([this.interestsNavData, ...interestsChildItems]);
                return setActiveItem(activeItemList);
            }

            // if interests does not have child items, retain existing items
            return this.feedNav.data ? this.feedNav.data.map(item => {
                item.isActive = false;
                return item;
            }) : null;
        }

        // Selected nav is not interests, show interests button
        this.showPersonalizeButton = true;

        const showPipeSeparator = this.showPipeSeparator && (home != null && this.selectedNavItem != null && home.id != this.selectedNavItem.id);
        // If the selected nav is not from config, we set the default feeds as the context.
        // This is our current solution until One Service is able to provide the context for these topics
        // Also we dont show the MyFeed if secondaryNavigation is enabled)
        if (!selectedNav) {
            let activeItemList: FeedNavigationItem[] = home != null ? [{ ...home, showPipeSeparator: showPipeSeparator }] : [];
            activeItemList = activeItemList.concat([
                { ...this.selectedNavItem },
                ...feeds
            ]);
            return setActiveItem(activeItemList);
        }

        let { children = [], parentId } = { ...selectedNav };

        // No parent, no children, show home and selected nav
        if (!parentId && !children.length) {
            const activeItemList: FeedNavigationItem[] = home != null ? [{ ...home, showPipeSeparator: showPipeSeparator }] : [];
            activeItemList.push(selectedNav);
            return setActiveItem(activeItemList);
        }

        // No parent but has children, 1st level contextual nav
        if (!parentId && children.length) {
            let activeItemList: FeedNavigationItem[] = home != null ? [{ ...home, showPipeSeparator: showPipeSeparator }] : [];
            activeItemList = activeItemList.concat([
                selectedNav,
                ...children
            ]);
            return setActiveItem(activeItemList);
        }

        // Has parent nav item, find the context
        let parentNav = { ...this.navItemMap.get(parentId) };
        const navItems = children.length ? [selectedNav, ...children] : [...parentNav.children];
        let contextParent;
        while (parentId && parentNav && this.displayBreadCrumb) {
            const { children: parentNavChildren = [] } = parentNav;

            if (!contextParent && parentNavChildren.length && children.length) {
                contextParent = parentNav;
                parentNav.showPipeSeparator = showPipeSeparator;
            }

            navItems.unshift(parentNav);

            parentId = parentNav.parentId;
            if (this.showOneParentBreadCrumb) {
                // set parentId to null after one level is set
                parentId = null;
            }

            if (!parentId && home != null) {
                navItems.unshift({ ...home, showPipeSeparator: showPipeSeparator && !contextParent });
            }

            children = parentNav.children;
            parentNav = { ...this.navItemMap.get(parentId) };
        }

        return setActiveItem(navItems);
    }

    /** Builds a Map of nav items for easier discovery of contextual nav */
    protected buildNavMap(): void {
        const { home, feeds } = this.navGroups || {};

        if (home) {
            this.navItemMap.set(home.id, home);
        }

        if (!feeds || !feeds.length) {
            return;
        }

        feeds.forEach(feed => {
            this.navItemMap.set(feed.id, feed);

            const { children } = feed;
            this.mapChildItems(children, feed.id);
        });
    }

    protected mapChildItems(children: NavigationItemInternal[] = [], parentId: string) {
        children.forEach(child => {
            this.navItemMap.set(child.id, { ...child, parentId });

            const { children: subChild } = child;

            if (subChild && subChild.length) {
                this.mapChildItems(subChild, child.id);
            }
        });
    }
}